import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useRecaptchaAction } from "../lib/useRecaptchaAction";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { RecaptchaDisclosure } from "../components/RecaptchaDisclosure";

export default function LoginPage() {
    const { login } = useAuth();
    const { getToken } = useRecaptchaAction();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const token = await getToken("login");
            const u = await login(email, password, token);
            toast.success("Bem-vindo de volta.");
            // Show subscription alert after login ONLY to the company manager
            try {
                const { data: me } = await (await import("../lib/api")).api.get("/auth/me");
                const sub = me?.subscription;
                const isManager = me?.role === "company_manager";
                if (isManager && sub?.warning && sub?.days_remaining !== undefined) {
                    toast.warning(`Subscrição termina em ${sub.days_remaining} dia(s) (${sub.ends_at?.slice(0, 10)}). Renove para evitar interrupções.`, { duration: 8000 });
                } else if (isManager && sub && sub.status === "expired") {
                    toast.error("A sua subscrição expirou. Adquira um novo plano para continuar.", { duration: 8000 });
                } else if (isManager && sub && sub.status === "pending") {
                    toast.info("A sua subscrição aguarda ativação pelo Super Admin.");
                } else if (isManager && sub && sub.status === "none") {
                    toast.info("Sem subscrição ativa. Adquira um plano para desbloquear todas as funcionalidades.", { duration: 8000 });
                }
            } catch (e) {
                // Subscription banner is best-effort: log to console but don't block login.
                console.warn("[login] post-login subscription banner suppressed:", e?.message || e);
            }
            nav("/app/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
            <aside className="relative bg-[#0d1628] text-white hidden lg:flex flex-col justify-between p-12 grain">
                <Link to="/" className="font-display text-3xl tracking-tight" data-testid="login-brand">IHTP</Link>
                <div>
                    <div className="font-display text-4xl xl:text-5xl leading-[1.1] max-w-lg">
                        Posso ainda não ter chegado onde queria, mas estou mais perto que ontem.
                    </div>
                    <div className="label-caps text-white/60 mt-8">IHTP — I Have The Power</div>
                </div>
                <div className="label-caps text-white/50">Plataforma oficial</div>
            </aside>

            <section className="flex items-center justify-center px-6 md:px-12 py-16">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-10">
                        <Link to="/" className="font-display text-3xl text-[#0d1628]">IHTP</Link>
                    </div>
                    <h1 className="font-display text-4xl text-[#0d1628]">Entrar</h1>
                    <p className="text-sm text-[#52525b] mt-3">Bem-vindo de volta. Entre na sua conta.</p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="login-form">
                        <div>
                            <label className="label-caps block mb-2">Email</label>
                            <input
                                type="email"
                                required
                                className="input-clean"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="label-caps block mb-2">Palavra-passe</label>
                            <div className="relative">
                                <input
                                    type={show ? "text" : "password"}
                                    required
                                    className="input-clean pr-10"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password"
                                    autoComplete="current-password"
                                />
                                <button type="button" className="absolute right-0 top-1 text-[#52525b]" onClick={() => setShow(!show)} data-testid="toggle-password">
                                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
                            {busy ? "A entrar..." : "Entrar"}
                        </button>
                    </form>

                    <div className="flex items-center justify-between mt-8 text-sm">
                        <Link to="/forgot-password" className="link-underline text-[#0d1628]" data-testid="forgot-link">
                            Esqueci-me da palavra-passe
                        </Link>
                        <Link to="/register" className="link-underline text-[#0d1628]" data-testid="register-link">
                            Registar empresa
                        </Link>
                    </div>
                    <RecaptchaDisclosure />
                </div>
            </section>
        </div>
    );
}
